import {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnLink,
  BtnNumberedList,
  BtnStyles,
  BtnUnderline,
  BtnUndo,
  type ContentEditableEvent,
  EditorProvider,
  Editor as RSEditor,
  Separator,
  Toolbar,
} from "react-simple-wysiwyg";
import sanitizeHtml from "sanitize-html";
import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { twMerge } from "tailwind-merge";

interface Props {
  value: string;
  onChange(event: { target: { value: string } }): void;
  className?: string;
  disabled?: boolean;
  /** Focus the rich-text area when the editor mounts (e.g. inline reply). */
  autoFocus?: boolean;
  /** Full toolbar by default; compact omits undo and block-style controls. */
  variant?: "full" | "compact";
  insertableVariables?: Array<{ label: string; token: string }>;
  /** Override HTML sanitization (default: sanitize-html defaults). */
  sanitizeHtmlFn?: (html: string) => string;
}

export const Editor: React.FC<Props> = ({
  value,
  onChange,
  className,
  disabled,
  autoFocus,
  variant = "full",
  insertableVariables,
  sanitizeHtmlFn,
}) => {
  const clean = sanitizeHtmlFn ?? sanitizeHtml;
  const variableOptions = useMemo(() => insertableVariables ?? [], [insertableVariables]);
  const [selectedVariable, setSelectedVariable] = useState<string>(variableOptions[0]?.token ?? "");
  const [toolbarRevision, setToolbarRevision] = useState(0);
  useEffect(() => {
    if (!selectedVariable && variableOptions.length > 0) {
      setSelectedVariable(variableOptions[0].token);
    }
  }, [selectedVariable, variableOptions]);

  const handleToolbarMouseDownCapture = (event: MouseEvent) => {
    if ((event.target as HTMLElement).closest("select")) return;
    event.preventDefault();
    // Toolbar buttons read queryCommandState during render; execCommand does not
    // trigger React updates, so refresh after the library button handler runs.
    requestAnimationFrame(() => {
      setToolbarRevision((revision) => revision + 1);
    });
  };
  void toolbarRevision;

  const handleChange = (event: ContentEditableEvent) => {
    onChange({ target: { value: clean(event.target.value) } });
  };

  const handleInsertVariable = () => {
    if (disabled || !selectedVariable) return;
    const inserted =
      typeof document !== "undefined" && document.execCommand("insertText", false, selectedVariable);
    if (!inserted) {
      onChange({ target: { value: clean(`${value}${selectedVariable}`) } });
    }
  };

  return (
    <EditorProvider>
      <RSEditor
        value={clean(value)}
        onChange={handleChange}
        className={twMerge("w-full min-w-0 max-w-full", className)}
        disabled={disabled}
        autoFocus={autoFocus}
        containerProps={{ className: "min-w-0 max-w-full" }}
      >
        <Toolbar onMouseDownCapture={handleToolbarMouseDownCapture}>
          {variant === "full" && (
            <>
              <BtnUndo />
              <Separator />
            </>
          )}
          <BtnBold />
          <BtnItalic />
          <BtnUnderline />
          <Separator />
          <BtnNumberedList />
          <BtnBulletList />
          <Separator />
          <BtnLink />
          <BtnClearFormatting />
          {variant === "full" && (
            <>
              <Separator />
              <BtnStyles />
            </>
          )}
          {variableOptions.length > 0 && (
            <>
              <Separator />
              <select
                aria-label="Template variables"
                className="rounded border border-gray-300 bg-white px-2 py-1 text-sm"
                value={selectedVariable}
                onChange={(event) => setSelectedVariable(event.target.value)}
                disabled={disabled}
              >
                {variableOptions.map((variable) => (
                  <option key={variable.token} value={variable.token}>
                    {variable.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleInsertVariable}
                disabled={disabled || !selectedVariable}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-sm disabled:opacity-60"
              >
                Insert
              </button>
            </>
          )}
        </Toolbar>
      </RSEditor>
    </EditorProvider>
  );
};
